// ResetPasswordForm.js
// WEST-599: step 2 of the reset flow, reached from the emailed link
//   /customer/{customerId}/reset-password?token=…
// On load the token is checked (not consumed) so an expired link says so immediately. The
// customer id in the URL is cosmetic: if the token belongs to a different customer the URL is
// corrected from the server's answer.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import { evaluatePassword, isPasswordValid, PasswordRulesList } from './passwordRules';
import './LoginForm.css';

const EXPIRED_MESSAGE = 'This password reset link is invalid or has expired. Please request a new one.';

const ResetPasswordForm = () => {
  const { customerId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { dataClient } = useOutletContext();

  const token = searchParams.get('token') || '';

  const [state, setState] = useState('checking'); // checking | ready | invalid | done
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [validation, setValidation] = useState(evaluatePassword(''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Validate the link once.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!/^[A-Za-z0-9_-]{43}$/.test(token)) {
        setState('invalid');
        return;
      }
      try {
        const response = await fetch(
          `${process.env.REACT_APP_PROXY_URL}/api/auth/reset/${encodeURIComponent(token)}`
        );
        const data = await response.json();
        if (cancelled) return;
        if (!data || !data.valid) {
          setState('invalid');
          return;
        }
        setLogin(data.login || '');
        // Correct a tampered or stale customer path so "Back to sign in" lands on the right county.
        if (data.customer_id != null && String(data.customer_id) !== String(customerId)) {
          navigate(`/customer/${data.customer_id}/reset-password?token=${encodeURIComponent(token)}`, { replace: true });
          return;
        }
        setState('ready');
      } catch (err) {
        console.error('Reset link check failed:', err);
        if (!cancelled) setState('invalid');
      }
    };
    check();
    return () => { cancelled = true; };
  }, [token, customerId, navigate]);

  if (!dataClient || state === 'checking') {
    return <LoadingSpinner />;
  }

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setValidation(evaluatePassword(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!isPasswordValid(validation)) {
      setError('Please fix the password requirements below.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_PROXY_URL}/api/auth/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });
      const data = await response.json().catch(() => ({}));
      // The .NET API serialises camelCase ({ result: { message } }); Node passes it through.
      const result = (data && (data.result || data.Result)) || {};
      const okMessage = result.message || result.Message;

      if (response.ok && okMessage) {
        setMessage(okMessage);
        setState('done');
        setPassword('');
        setConfirm('');
        return;
      }
      if (response.status === 429) {
        setError('Too many attempts. Please wait a few minutes and try again.');
        return;
      }
      const serverError = result.error || result.Error || EXPIRED_MESSAGE;
      // An invalid/expired token is terminal for this page; a weak password is not.
      if (/invalid or has expired/i.test(serverError)) {
        setState('invalid');
      } else {
        setError(serverError);
      }
    } catch (err) {
      console.error('Password reset failed:', err);
      setError('The password could not be changed right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'invalid') {
    return (
      <div className="user-form">
        <div className="error">{EXPIRED_MESSAGE}</div>
        <p>
          <Link to={`/customer/${customerId}/forgot-password`}>Request a new link</Link>
          {' | '}
          <Link to={`/customer/${customerId}/login`}>Back to sign in</Link>
        </p>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div className="user-form">
        <div className="message">{message}</div>
        <p>
          <Link to={`/customer/${customerId}/login`}>Sign in</Link>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      <table className="tableMain" border="0" width="100%" cellPadding="2" cellSpacing="2">
        <tbody>
          <tr>
            <td colSpan="2">
              <p>
                Choose a new password{login ? <> for <strong>{login}</strong></> : null}.
              </p>
              {error && <div className="error">{error}</div>}
            </td>
          </tr>
          <tr>
            <td className="lbltxt">New password:</td>
            <td>
              <input
                className="fieldsSm"
                type="password"
                name="newPassword"
                autoComplete="new-password"
                autoFocus
                required
                value={password}
                onChange={handlePasswordChange}
              />
              <PasswordRulesList validation={validation} />
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Confirm password:</td>
            <td>
              <input
                className="fieldsSm"
                type="password"
                name="confirmPassword"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <td></td>
            <td>
              <input
                type="submit"
                value={submitting ? 'Saving…' : 'Change password'}
                disabled={submitting || !isPasswordValid(validation) || password !== confirm}
              />
              {' '}
              <Link to={`/customer/${customerId}/login`}>Cancel</Link>
            </td>
          </tr>
        </tbody>
      </table>
    </form>
  );
};

export default ResetPasswordForm;
